/**
 * Typewriter hook - Creates typewriter animation effect with erase
 * Types word, pauses, erases, then types next word
 */

import {useState, useEffect} from 'react';

interface UseTypewriterOptions {
  words: string[];
  typingSpeed?: number;
  erasingSpeed?: number;
  pauseTime?: number;
}

export function useTypewriter({
  words,
  typingSpeed = 100,
  erasingSpeed = 50,
  pauseTime = 2000,
}: UseTypewriterOptions) {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[currentWordIndex];

    if (!isDeleting && currentText.length < currentWord.length) {
      // Typing
      const timeout = setTimeout(() => {
        setCurrentText(currentWord.slice(0, currentText.length + 1));
      }, typingSpeed);
      return () => clearTimeout(timeout);
    } else if (!isDeleting && currentText.length === currentWord.length) {
      // Finished typing, pause then start deleting
      const timeout = setTimeout(() => {
        setIsDeleting(true);
      }, pauseTime);
      return () => clearTimeout(timeout);
    } else if (isDeleting && currentText.length > 0) {
      // Deleting
      const timeout = setTimeout(() => {
        setCurrentText(currentText.slice(0, -1));
      }, erasingSpeed);
      return () => clearTimeout(timeout);
    } else if (isDeleting && currentText.length === 0) {
      // Finished deleting, move to next word
      setIsDeleting(false);
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }
  }, [currentText, isDeleting, currentWordIndex, words, typingSpeed, erasingSpeed, pauseTime]);

  return currentText;
}

